import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { isNonEmptyString } from "src/services/predicates";
import * as Url from "src/services/url";
import {
  EXPLORE_SCHOOLS_TOKEN,
  LOGIN_PREFILL_TOKEN_PARAM,
} from "src/types/searchParams";

export function useRedirectToLandingPage() {
  const [params] = useSearchParams();
  const orgPath = useOrganizationPath();
  const navigate = useNavigate();

  return useCallback((): boolean => {
    if (!isNonEmptyString(orgPath)) {
      return false;
    }

    // Check if this is a direct link to login/signup page
    // This should skip landing page
    const page = params.get("page");
    if (page === "login" || page === "signup") {
      return false;
    }

    // Prefill tokens must reach RequireAuth so they can be forwarded to Auth0.
    const loginPrefillToken = params.get(LOGIN_PREFILL_TOKEN_PARAM);
    if (isNonEmptyString(loginPrefillToken)) {
      return false;
    }

    // Auth0 display controls must remain on the direct authentication flow.
    const hide = params.get("hide");
    if (isNonEmptyString(hide)) {
      return false;
    }

    // Check if explore saved school exists in the query params
    // This should skip landing page
    const exploreSavedSchools = params.get(EXPLORE_SCHOOLS_TOKEN);
    if (isNonEmptyString(exploreSavedSchools)) {
      return false;
    }

    // Check if user tries to go to a direct page, most likely a bookmark link
    // e.g: /:org/payments or /:org/admin/payments
    // This should skip landing page
    const pathname = window.location.pathname.toLowerCase();
    const orgRoots = [`/${orgPath}`, `/${orgPath}/admin`];
    const isOrgRoot = orgRoots.includes(pathname.replace(/\/+$/, ""));
    if (!isOrgRoot) {
      return false;
    }

    // Redirect to landing page
    // redirect to /:org/login
    navigate(
      Url.Parent.Landing.index(
        { path: orgPath },
        Object.fromEntries(params.entries())
      ),
      { replace: true }
    );
    return true;
  }, [navigate, orgPath, params]);
}
